import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import VideoCard from '@/components/base/VideoCard';
import DetailPlayer from '@/pages/detail/components/DetailPlayer';
import { videoItems } from '@/mocks/videos';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = videoItems.find((v) => v.id === id);
  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState('');

  const related = useMemo(() => {
    if (!item) return [];
    const same = videoItems.filter((v) => v.categorySlug === item.categorySlug && v.id !== item.id);
    const others = videoItems.filter((v) => v.categorySlug !== item.categorySlug && v.id !== item.id);
    return [...same, ...others].slice(0, 4);
  }, [item]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!item) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <p className="font-heading text-7xl font-black text-primary-100 md:text-9xl">404</p>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的视频可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-5-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const toggleFavorite = () => {
    setFavorited((v) => {
      showToast(v ? '已取消收藏' : '收藏成功');
      return !v;
    });
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-400">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${item.categorySlug}`} className="transition-colors hover:text-primary-600">
          {item.category}分类
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="line-clamp-1 text-foreground-700">{item.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">{item.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line" />
              {item.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {item.uploadDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line" />
              {item.type}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-earth-line" />
              {item.region}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {item.duration}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1 rounded-md bg-accent-100 px-3 py-1.5 text-sm font-bold text-accent-700">
              <i className="ri-star-fill" />
              {item.rating}
            </span>
            <span className="flex items-center gap-1.5 rounded-md bg-background-100 px-3 py-1.5 text-sm text-foreground-500">
              <i className="ri-fire-line" />
              {item.views}
            </span>
            <span className="flex items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-1.5 text-sm text-secondary-800">
              <i className="ri-refresh-line" />
              {item.updateStatus}
            </span>
          </div>

          <div className="mt-6">
            <DetailPlayer item={item} />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={toggleFavorite}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              <i className={favorited ? 'ri-heart-3-fill text-lg' : 'ri-heart-3-line text-lg'} />
              {favorited ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={() => showToast('分享链接已复制（演示）')}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-share-forward-line text-lg" />
              分享
            </button>
          </div>

          <section className="mt-8 rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-900">内容简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.description}</p>

            <h3 className="mt-6 font-heading text-base font-bold text-foreground-900">精彩看点</h3>
            <ul className="mt-3 flex flex-col gap-2.5">
              {item.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-5">
              <span className="text-sm text-foreground-400">标签：</span>
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${item.categorySlug}`}
                  className="rounded-md bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-600"
                >
                  #{tag}
                </Link>
              ))}
              <Link
                to={`/category/${item.categorySlug}`}
                className="ml-auto flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                查看所属分类
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
              <i className="ri-magic-line text-primary-500" />
              相关推荐
            </h2>
            <div className="mt-4 flex flex-col gap-4">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/detail/${rel.id}`}
                  className="group flex gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
                >
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={rel.cover}
                      alt={rel.title}
                      title={`${rel.title} - 相关推荐`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="line-clamp-1 text-sm font-medium text-foreground-800 group-hover:text-primary-600">
                      {rel.title}
                    </h3>
                    <p className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                      <span>{rel.type}</span>
                      <span>·</span>
                      <span>{rel.year}</span>
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-accent-600">
                      <i className="ri-star-fill" />
                      {rel.rating}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-12">
        <h2 className="mb-5 font-heading text-xl font-bold text-foreground-900">更多同分类内容</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((rel) => (
            <VideoCard key={rel.id} item={rel} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-foreground-900 px-4 py-2.5 text-sm text-background-50 shadow-lg md:bottom-10">
          {toast}
        </div>
      )}
    </div>
  );
}